import React, { PropsWithChildren, useContext, useEffect } from "react";
import Footer from "../components/Footer/Footer";
import Header from "../components/Header/Header";
import { Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { AccountContext } from "../store/AccountContext";
import HeaderInteral from "../components/Header/HeaderInternal";

/**
 * PM5 T1 (chrome, batch "layouts") — converted from MUI v4 + `makeStyles` to
 * MUI v5+ (`@mui/material`) `sx`. See docs/mui-conversion-guide.md.
 *
 * Every `<Box>` here carried v4 *system props* (`margin={0}`, `display="flex"`,
 * …). v9's `createBox` only reads `sx` and forwards anything else to the DOM,
 * so each shorthand is moved into `sx` verbatim (guide §2, "Box system props").
 * Values keep their meaning: numbers on spacing props are spacing multiples
 * (`paddingBottom: 4` = 32px, `theme.spacing` is 8 on both themes).
 *
 * The v4 `useStyles` also had a `loading` key that no element in this file ever
 * referenced — dropped rather than carried forward as an unused `sx` constant.
 */

// was: the outer <Box margin={0} padding={0} display="flex" minHeight="100vh"
// flexDirection="column" justifyContent="space-between">
const rootSx: SxProps<Theme> = {
  margin: 0,
  padding: 0,
  display: "flex",
  minHeight: "100vh",
  flexDirection: "column",
  justifyContent: "space-between",
};

// was: the middle <Box margin={0} padding={0} display="flex"
// flexDirection="column" flex={1}>
const columnSx: SxProps<Theme> = {
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  flex: 1,
};

// was: the inner Box's system props PLUS `className={classes.background}`,
// merged into one object.
//
// The merge order reproduces v4's cascade and must not be reshuffled. In v4 the
// Box's own system-prop class and the `makeStyles` class both had single-class
// specificity, so the winner was source order — and `makeStyles` assigns its
// sheet index at module-evaluation time, after the imported `Box` module has
// already taken its own. The layout's sheet therefore injected *later* and won,
// which means `paddingBottom: 32px` beat the Box's `padding: 0px` on the bottom
// edge (left/right/top stayed 0). Listing `padding` before `paddingBottom` here
// emits the declarations in that same order, so the computed padding is
// unchanged: 0 / 0 / 32px / 0.
//
// `flexGrow: 1` appears in both v4 sources with the same value, so it collapses
// to one declaration.
const backgroundSx: SxProps<Theme> = {
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  alignItems: "center",
  width: "100%",
  height: "100%",
  // v4 needed a `@ts-ignore` here because the v4 theme's `TypeBackground` was
  // never augmented; `themeV5.ts` declares `ffNavyLighter`, so the sx palette
  // path resolves and type-checks.
  backgroundColor: "background.ffNavyLighter",
  paddingBottom: 4,
};

const DefaultLayout = ({
  children,
  showTry = true,
  showSignIn = true,
}: PropsWithChildren<{ showTry?: boolean; showSignIn?: boolean }>) => {
  const { loggedIn } = useContext(AccountContext);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={rootSx}>
      <Box sx={columnSx}>
        {!loggedIn && <Header showTry={showTry} showSignIn={showSignIn} />}
        {loggedIn && <HeaderInteral />}
        <Box sx={backgroundSx}>{children}</Box>
      </Box>
      <Footer />
    </Box>
  );
};

export default DefaultLayout;
